import type { FieldEditorValues, SectionEditorValues } from './config';
import { humaniseIdentifier } from './humaniseIdentifier';

export type UniqueIdentifier = string | number;

export interface FieldSummary {
  name: string;
  type: string;
  required: boolean;
  readOnly?: boolean;
  hidden?: boolean;
  isCustom?: boolean;
  forcedRequired?: boolean;
  canEditLabel?: boolean;
  canEditOptions?: boolean;
  canDelete?: boolean;
  subtitle?: string;
  isGroup?: boolean;
  originalLabel?: string;
}

export const DEFAULT_LANG = {
  formEditor: {
    title: 'Configure form',
    description: 'Drag and drop to reorder sections and fields',
    save: 'Save',
    cancel: 'Cancel',
    addField: 'Add field',
    addSection: 'Add section',
  },
  sectionBuilder: {
    title: 'Section editor',
    addSave: 'Add section',
    editSave: 'Edit section',
    cancel: 'Cancel',
  },
  fieldEditor: {
    title: 'Field editor',
    addSave: 'Add field',
    editSave: 'Save',
    cancel: 'Cancel',
  },
};

export const getFieldTypeLabel = (
  type: string,
  options?: { value: string; label: string }[]
): string => {
  if (options) {
    const match = options.find((o) => o.value === type);
    if (match) {
      return match.label;
    }
  }

  return humaniseIdentifier(type);
};

export const buildSectionDefaults = (
  sectionId: string | null,
  sectionNames: Record<string, string>,
  sectionConfigs: Record<string, SectionEditorValues>
): SectionEditorValues => {
  if (!sectionId) {
    return {
      name: '',
      description: '',
      conditionalLogicEnabled: false,
      conditionalLogicOperation: 'and' as const,
      conditionalLogicRules: [],
      guidanceEnabled: false,
      guidance: '',
    };
  }
  const config = sectionConfigs[sectionId];
  if (config) {
    return config;
  }

  return {
    name: sectionNames[sectionId] ?? '',
    description: '',
    conditionalLogicEnabled: false,
    conditionalLogicOperation: 'and' as const,
    conditionalLogicRules: [],
    guidanceEnabled: false,
    guidance: '',
  };
};

/**
 * Maps a host-supplied field into the editor's summary state when the dialog
 * opens.
 *
 * `config` is the field editor's own value shape and wins when present. Hosts
 * emit it only for fields that carry options, conditional logic or a persisted
 * default value, so a field that is *merely* hidden or read-only arrives with
 * no `config` at all — hence the fall back to the top-level flags. Reading
 * `config` alone rehydrated both as false, and the next save wrote that back,
 * silently unhiding the field (CUS-321).
 *
 * Typed structurally rather than against `FormEditorFieldData` to keep this
 * module free of an import cycle with `./types`.
 */
export const buildFieldSummary = (
  field: FieldSummary & { config?: FieldEditorValues }
): FieldSummary => ({
  name: field.name,
  type: field.type,
  required: field.required,
  readOnly: field.config?.readOnly ?? field.readOnly ?? false,
  hidden: field.config?.hidden ?? field.hidden ?? false,
  isCustom: field.isCustom,
  forcedRequired: field.forcedRequired,
  canEditLabel: field.canEditLabel,
  canEditOptions: field.canEditOptions,
  canDelete: field.canDelete,
  subtitle: field.subtitle,
  isGroup: field.isGroup,
  originalLabel: field.originalLabel,
});

export const buildFieldDefaults = (
  fieldId: string | null,
  fields: Record<string, FieldSummary>,
  fieldConfigs: Record<string, FieldEditorValues>
): FieldEditorValues => {
  if (!fieldId) {
    return {
      fieldType: 'text',
      fieldName: '',
      required: false,
      readOnly: false,
      hidden: false,
      defaultValueEnabled: false,
      defaultValue: '',
      options: [],
      conditionalLogicEnabled: false,
      conditionalLogicOperation: 'and' as const,
      conditionalLogicRules: [],
      guidanceEnabled: false,
      guidance: '',
    };
  }
  const config = fieldConfigs[fieldId];
  if (config) {
    return config;
  }

  const field = fields[fieldId];

  return {
    fieldType: field?.type ?? '',
    fieldName: field?.name ?? '',
    required: field?.required ?? false,
    readOnly: field?.readOnly ?? false,
    hidden: field?.hidden ?? false,
    defaultValueEnabled: false,
    defaultValue: '',
    options: [],
    conditionalLogicEnabled: false,
    conditionalLogicOperation: 'and' as const,
    conditionalLogicRules: [],
    guidanceEnabled: false,
    guidance: '',
  };
};
